/**
 * MapArrowsToggle — shared toggle for the visibility of direction
 * arrows (the small chevrons painted along the polyline by the
 * shared `<DirectionArrows>` component).
 *
 * Mirrors the API of MapTooltipToggle so every map can compose
 * "Show/Hide etiquetas" + "Show/Hide setas" in a consistent cluster.
 *
 * b99.9.36 — added per user request: arrows should be on by default
 * but the user wants the option to clear them for screenshots or
 * when the route detail is dense enough that the chevrons feel
 * crowded.
 */
import { createContext, useCallback, useState } from "react";
import { Navigation, NavigationOff } from "lucide-react";
import { Button } from "@/components/ui/button";

export const MapArrowsContext = createContext(true);

export function useArrowsToggle(defaultVisible = true) {
  const [visible, setVisible] = useState(defaultVisible);
  const toggle = useCallback(() => setVisible((v) => !v), []);
  return { visible, toggle };
}

export default function MapArrowsToggle({
  visible,
  onClick,
  className = "",
  size = "sm",
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      onClick={onClick}
      className={`bg-background/90 backdrop-blur-sm ${className}`}
      title={visible ? "Esconder setas de direcção" : "Mostrar setas de direcção"}
      aria-label={visible ? "Esconder setas de direcção" : "Mostrar setas de direcção"}
      data-testid="map-arrows-toggle"
      data-state={visible ? "on" : "off"}
    >
      {visible ? (
        <Navigation className="h-4 w-4" />
      ) : (
        <NavigationOff className="h-4 w-4 text-muted-foreground" />
      )}
    </Button>
  );
}
